import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications, useNotificationCount } from '../hooks/useInvestQueries';
import { Bell, Check, X, Trash2 } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';

export default function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: notifications = [] } = useNotifications();
  const { data: unreadCount = 0 } = useNotificationCount();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      // Just mark as read silently - visual update (dot removal) is enough feedback
      markAsRead(notification.notification_id);
    }
    
    setIsOpen(false);
    
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-count']);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-count']);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${notificationId}`);
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-count']);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Format time ago
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Get icon color based on notification type
  const getTypeColor = (type) => {
    switch (type) {
      case 'commitment_approved': return 'text-emerald-400';
      case 'commitment_rejected': return 'text-red-400';
      case 'payout_available': return 'text-[#D4AF37]';
      default: return 'text-blue-400';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-[#94A3B8]" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-[#0A1128] text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Mobile Backdrop */}
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setIsOpen(false)}
          />
          
          <div className="fixed md:absolute left-1/2 md:left-auto md:right-0 top-20 md:top-12 -translate-x-1/2 md:translate-x-0 w-[calc(100%-32px)] max-w-md md:w-96 bg-[#0F172A] border border-[#334155] rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-[70vh] md:max-h-none">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#334155] shrink-0">
              <h3 className="font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-[#D4AF37] hover:text-[#F4C542] transition-colors flex items-center gap-1"
                >
                  <Check className="w-3 h-3" />
                  Mark all read
                </button>
              )}
            </div>
  
            {/* Notification List */}
            <div className="overflow-y-auto custom-scrollbar max-h-[60vh]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-[#94A3B8]">
                  <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No notifications yet</p>
                </div>
              ) : (
                <>
                  {notifications.slice(0, 5).map((notification) => (
                    <div
                      key={notification.notification_id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`group p-4 border-b border-[#334155]/50 hover:bg-[#1E293B] transition-colors cursor-pointer relative ${
                        !notification.read ? 'bg-[#D4AF37]/5' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Unread Indicator */}
                        <div className="mt-1.5 shrink-0">
                          {!notification.read ? (
                            <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-[#334155]" />
                          )}
                        </div>
  
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${getTypeColor(notification.type)}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-[#94A3B8] mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-[#64748B] mt-2">
                            {timeAgo(notification.created_at)}
                          </p>
                        </div>
  
                        {/* Delete Button */}
                        <button
                          onClick={(e) => deleteNotification(notification.notification_id, e)}
                          className="opacity-100 sm:opacity-0 group-hover:opacity-100 p-1.5 hover:bg-white/10 rounded transition-all absolute top-2 right-2 sm:static"
                          aria-label="Delete notification"
                        >
                          <Trash2 className="w-4 h-4 text-[#64748B] hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  <div className="p-3 text-center border-t border-[#334155] bg-[#0F172A]">
                    <button 
                      onClick={() => { setIsOpen(false); navigate('/notifications'); }}
                      className="text-sm text-[#D4AF37] hover:text-white font-medium transition-colors w-full py-2 hover:bg-[#1E293B] rounded-lg"
                    >
                      View All Notifications
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
